/**
 * Org structure — Compare.
 *
 * Each column is self-contained: every ancestor from the top of the tree, then
 * the unit itself, then what reports to it on a drawn spine. Clicking an
 * ancestor or a subordinate moves *that* column only — the others stay where
 * they are, which is the whole point of putting two districts side by side.
 */
import { Fragment } from 'react';
import Icon from './org-icons';
import { avatarStyle, initials, headColour, headLabel, nf, vacancyPct, vacancyTone, peopleLabel, type OrgModel, type OrgNode } from './org-model';
import styles from './org-structure.module.css';

/** How many units the compare set holds. Past four the grid scrolls sideways
 *  rather than squeezing columns, so the ceiling is about memory, not layout. */
export const MAX_COMPARE = 12;

interface CompareProps {
  model: OrgModel;
  columns: string[];
  onSetColumn: (index: number, id: string) => void;
  onRemoveColumn: (index: number) => void;
  onToggleCompare: (id: string) => void;
  onClear: () => void;
  /** Jump to the Hierarchy view centred on this unit. */
  onShowOnChart: (id: string) => void;
  onOpenUnit: (id: string) => void;
  onEditUnit?: (id: string) => void;
  onAddUnder?: (id: string) => void;
}

const Avatar = ({ node }: { node: OrgNode }) => (
  <span
    className={styles.av}
    style={avatarStyle(node)}
    aria-hidden="true"
  >
    {node.head ? initials(node.head.name) : '—'}
  </span>
);

const OrgCompare = ({
  model,
  columns,
  onSetColumn,
  onRemoveColumn,
  onToggleCompare,
  onClear,
  onShowOnChart,
  onOpenUnit,
  onEditUnit,
  onAddUnder,
}: CompareProps) => {
  // Sorted shallowest-first so the picker reads like the tree it came from.
  const options = model.ids
    .map((id) => model.byId[id])
    .sort((a, b) => a.lvl - b.lvl || a.name.localeCompare(b.name));

  const picker = (
    <select
      value=""
      aria-label="Add a unit to the compare set"
      onChange={(e) => {
        if (e.target.value) onToggleCompare(e.target.value);
      }}
    >
      <option value="">Add a unit…</option>
      {options
        .filter((u) => !columns.includes(u.id))
        .map((u) => (
          <option key={u.id} value={u.id}>
            {'— '.repeat(u.lvl)}
            {u.name}
          </option>
        ))}
    </select>
  );

  if (!columns.length) {
    return (
      <div className={styles.scroll}>
        <div className={styles.pad}>
          <div className={styles.cmpempty}>
            <h3>Nothing in the compare set yet</h3>
            <p>
              Pick units and they stay here while you work. Click the <span className={styles.pin}>+</span> on any card
              in Hierarchy, Lateral or Compact — or choose one below. Up to {MAX_COMPARE} at a time.
            </p>
            {picker}
          </div>
        </div>
      </div>
    );
  }

  /* Four fit the width. Past that, fix the column width and scroll — squeezing
     eight columns into a screen makes none of them readable. */
  const wide = columns.length > 4;

  const column = (id: string, index: number) => {
    const u = model.byId[id];
    if (!u) return null;
    return (
      <div className={styles.cmpcol} key={`${id}-${index}`}>
        <div className={styles.cmphd}>
          <select
            value={id}
            aria-label={`Unit in column ${index + 1}`}
            onChange={(e) => onSetColumn(index, e.target.value)}
          >
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {'— '.repeat(o.lvl)}
                {o.name}
              </option>
            ))}
          </select>
          <button aria-label="Take it out of the compare set"
            type="button"
            className={styles.iact}
            title="Take it out of the compare set"
            onClick={() => onRemoveColumn(index)}
          >
            <Icon name="x" className={styles.ic} size={13} />
          </button>
        </div>

        <div className={styles.cmpbody}>
          <div className={styles.lin}>
            {u.path.length ? (
              u.path.map((a) => {
                const anc = model.byId[a];
                return (
                  <Fragment key={a}>
                    <button
                      type="button"
                      className={styles.anc}
                      title={`Put ${anc.name} in this column instead`}
                      onClick={() => onSetColumn(index, a)}
                    >
                      <span className={styles.lvl}>L{anc.lvl + 1}</span>
                      <span className={styles.nm}>{anc.name}</span>
                      <span className={styles.lvl}>{anc.typeLabel}</span>
                    </button>
                    <span className={styles.drop} />
                  </Fragment>
                );
              })
            ) : (
              <p style={{ fontSize: '.71875rem', color: 'var(--org-ink-3)', margin: '0 0 .4rem' }}>
                Top of the tree — nothing sits above it.
              </p>
            )}

            <div className={styles.selfcard}>
              <div className={styles.band} style={{ background: headColour(u) }} />
              <div className={styles.selfin}>
                <p className={styles.nm}>{u.name}</p>
                <p className={styles.cd}>
                  {u.code} · {u.typeLabel.toUpperCase()}
                </p>
                <div className={styles.who}>
                  <Avatar node={u} />
                  <span className={`${styles.n}${u.headState === 'vacant' ? ` ${styles.vacname}` : ''}`}>
                    {headLabel(u)}
                  </span>
                  {u.headRank && <span className={styles.rk}>{u.headRank}</span>}
                </div>
                <div className={styles.figs}>
                  <span className={styles.fg}>
                    <b>People</b>
                    <span>{peopleLabel(model, u)}</span>
                  </span>
                  <span className={styles.fg}>
                    <b>Posts</b>
                    <span>
                      {nf(u.rFilled)}/{nf(u.rPosts)}
                    </span>
                  </span>
                  <span className={styles.fg}>
                    <b>Unfilled</b>
                    <span style={{ color: vacancyTone(vacancyPct(u)) }}>
                      {vacancyPct(u) === null ? '—' : `${vacancyPct(u)}%`}
                    </span>
                  </span>
                  <span className={styles.fg}>
                    <b>Sub-units</b>
                    <span>{nf(u.subUnits)}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <p className={styles.kidhd}>
            Reports to this unit <span>{u.kids.length}</span>
          </p>
          {u.kids.length ? (
            <div className={styles.kidlist}>
              {u.kids.map((k) => {
                const c = model.byId[k];
                return (
                  <button
                    key={k}
                    type="button"
                    className={styles.kidrow}
                    title={`Put ${c.name} in this column instead`}
                    onClick={() => onSetColumn(index, k)}
                  >
                    <span
                      className={styles.sq}
                      style={{ background: headColour(c) }}
                    />
                    <span className={styles.nm}>
                      {c.name}
                      <i>
                        {c.head ? c.head.name : (
                          <span className={c.headState === 'vacant' ? styles.vacname : undefined}>
                            {headLabel(c).toLowerCase()}
                          </span>
                        )}
                        {c.headRank ? ` · ${c.headRank}` : ''}
                      </i>
                    </span>
                    <span className={styles.n}>{nf(c.people)}</span>
                    {c.kids.length ? (
                      <Icon name="chev" className={`${styles.ic} ${styles.go}`} size={12} />
                    ) : (
                      <span style={{ width: 12 }} />
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className={styles.nokids}>Last level — nothing reports to it.</p>
          )}
        </div>

        <div className={styles.cmpfoot}>
          <button aria-label="Switch to the Hierarchy view, centred on this unit"
            type="button"
            className={styles.btn}
            style={{ flex: 1, justifyContent: 'center' }}
            title="Switch to the Hierarchy view, centred on this unit"
            onClick={() => onShowOnChart(id)}
          >
            Show on the chart
          </button>
          <button aria-label="Open the unit record" type="button" className={styles.iact} title="Open the unit record" onClick={() => onOpenUnit(id)}>
            <Icon name="out" className={styles.ic} size={13} />
          </button>
          {onEditUnit && (
            <button aria-label="Edit" type="button" className={styles.iact} title="Edit" onClick={() => onEditUnit(id)}>
              <Icon name="edit" className={styles.ic} size={13} />
            </button>
          )}
          {onAddUnder && (
            <button aria-label="Add a unit under this" type="button" className={styles.iact} title="Add a unit under this" onClick={() => onAddUnder(id)}>
              <Icon name="plus" className={styles.ic} size={13} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.scroll}>
      <div className={styles.pad}>
        <div className={styles.cmpbar}>
          <span className={styles.cmplbl}>
            Comparing <b>{columns.length}</b> of {MAX_COMPARE} stored
            {wide ? ' · scroll sideways for the rest' : ''}
          </span>
          {columns.length < MAX_COMPARE ? (
            picker
          ) : (
            <span className={styles.cmplbl}>The set is full — remove one to add another.</span>
          )}
          <button aria-label="Empty the compare set" type="button" className={styles.btn} title="Empty the compare set" onClick={onClear}>
            Clear the set
          </button>
          <span className={styles.cmplbl} style={{ marginLeft: 'auto' }}>
            Click any ancestor or subordinate to move that column. The others stay put.
          </span>
        </div>

        <div className={styles.cmpscroll}>
          <div
            className={styles.cmpcols}
            style={
              wide
                ? { gridAutoFlow: 'column', gridAutoColumns: '20rem' }
                : { gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }
            }
          >
            {columns.map(column)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgCompare;
